/**
 * SUB-4.2 · FR34, FR35. Which account a row in a merged list belongs to, and
 * the account list a merged filter may offer.
 *
 * ⚠️ THIS MODULE NAMES ACCOUNTS. IT DOES NOT BOUND ANYTHING.
 *
 * ISO4 says the merged bound is applied at the data layer and never by
 * post-filtering in application code. Everything here runs after the rows have
 * already arrived, so nothing in this file may ever be the reason a row is
 * absent. The set of accounts a list can contain is `merged_scope_tenant_ids()`
 * evaluated inside `projects_select` / `tasks_select`; the set this file offers
 * as filter options is a subset of the set the server already returned. An
 * account filter is a NARROWING WITHIN an already-bounded set, which is why it
 * is applied as a `.eq('tenant_id', …)` on the query rather than as an
 * `Array.filter` on the result: a narrowing the server performs cannot be
 * mistaken for the bound, and a narrowing naming an account outside the subtree
 * returns nothing rather than widening.
 *
 * ⚠️ NAMES COME FROM `userTenants`, NOT FROM A JOIN. `tenants_select` is still
 * `tenant_id = get_current_user_tenant_id()` plus a global-role branch, so a
 * `projects → tenants` join would return the anchor's name and NULL for every
 * sub-account, and the global-role branch it would otherwise lean on is F-13,
 * an open finding. `userTenants` is the picker's own source, built from the
 * caller's `users.tenant_id` plus their `user_roles` rows, and an Agency Admin
 * holds a SUB-2.6 grant in every sub-account. That is the branch F-13's fix is
 * required to keep (`security-findings.md:1818`), so this survives it.
 */

/** The shape this module needs from a tenant row. `Tenant` is a superset. */
export interface NameableTenant {
  tenant_id: string;
  company_name?: string | null;
}

/** One account, as a merged list labels it. */
export interface AccountLabel {
  tenantId: string;
  /** What to render. Never empty. */
  name: string;
  /**
   * Whether `name` is the account's real name or a stand-in built from its id.
   *
   * ⚠️ False is not an error state to hide. A row whose account cannot be named
   * still has to say which account it is: an EMPTY Account cell reads as "mine",
   * which is precisely the mis-attribution FR34 exists to prevent.
   */
  known: boolean;
}

/** Names keyed by tenant id, built once per render from the picker's own list. */
export function buildAccountNames(
  tenants: readonly NameableTenant[] | null | undefined,
): Map<string, string> {
  const names = new Map<string, string>();
  for (const tenant of tenants ?? []) {
    const name = tenant.company_name?.trim();
    if (tenant.tenant_id && name) names.set(tenant.tenant_id, name);
  }
  return names;
}

/**
 * Label one row's account.
 *
 * A null or empty `tenantId` is the one case that cannot be labelled at all, and
 * it is reported as such rather than silently rendered as the current account.
 */
export function accountLabelFor(
  tenantId: string | null | undefined,
  names: ReadonlyMap<string, string>,
): AccountLabel {
  if (!tenantId) {
    return { tenantId: '', name: 'Unattributed', known: false };
  }
  const name = names.get(tenantId);
  if (name) return { tenantId, name, known: true };
  return { tenantId, name: `Account ${tenantId.slice(0, 8)}`, known: false };
}

/**
 * FR35. The accounts a merged list may be filtered down to.
 *
 * Built from the ids the SERVER reported for this scope, never from the tenant
 * rows the client happens to hold. Offering an account the resolver did not
 * return would be a filter that always yields nothing; omitting one it did
 * return would hide rows the list is already showing.
 *
 * Sorted by rendered name so the order matches the sortable Account column, with
 * unnamed accounts last rather than interleaved under a stand-in name.
 */
export function accountFilterOptions(
  tenantIds: readonly string[],
  names: ReadonlyMap<string, string>,
): AccountLabel[] {
  return tenantIds
    .map((id) => accountLabelFor(id, names))
    .sort((a, b) => {
      if (a.known !== b.known) return a.known ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
}

/**
 * The sort key for the Account column (design B1).
 *
 * Case-folded so "acme" and "Acme" do not split the run, and unnamed accounts
 * sort after named ones in ascending order for the same reason they are listed
 * last: a stand-in built from a uuid carries no meaning to sort on.
 */
export function accountSortKey(
  tenantId: string | null | undefined,
  names: ReadonlyMap<string, string>,
): string {
  const label = accountLabelFor(tenantId, names);
  return `${label.known ? '0' : '1'}${label.name.toLocaleLowerCase()}`;
}
